import {
  isConnectionInactive,
  type IFilteredConnection,
} from '@/modules/integrations';
import type { V2BookConnectionDescriptor } from '@/generated/hooks/bdk/models';

/**
 * Resolves the connection descriptors behind a selected connection row.
 *
 * A row can stand for a draft/published pair sharing one name, so both are
 * returned — reactivating only one would leave the other scaled down.
 *
 * @param connections - Connection rows offered by the dialog
 * @param connectionId - Currently selected connection id
 * @returns Descriptors for the selected row, empty when nothing matches
 */
export function getSelectedConnectionDescriptors(
  connections: IFilteredConnection[],
  connectionId?: string,
): V2BookConnectionDescriptor[] {
  const selected = connections.find(
    connection => connection.connection_id === connectionId,
  );
  if (!selected) return [];

  return [selected.draft_connection, selected.published_connection].filter(
    (connection): connection is V2BookConnectionDescriptor => !!connection,
  );
}

/**
 * Whether the selected connection was scaled to zero for inactivity, in which
 * case saving a trigger against it fails until it is reactivated.
 *
 * Only the selected id counts, since the trigger is created against that one
 * connection even though reactivating brings its sibling stage back up too.
 *
 * @param connections - Connection rows offered by the dialog
 * @param connectionId - Currently selected connection id
 * @returns True when the selection needs a reactivation first
 */
export function isSelectedConnectionInactive(
  connections: IFilteredConnection[],
  connectionId?: string,
): boolean {
  return getSelectedConnectionDescriptors(connections, connectionId).some(
    connection =>
      connection.connection_id === connectionId &&
      isConnectionInactive(connection),
  );
}
